import React, { useState } from 'react';
import Icon from '../Icon';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import ProcSection from './ProcSection';
import { FormInput, FormSelect, FormTextarea } from '../FormField';
import { formatDecimal } from '../../utils/decimal';
import { fmtQty } from './procurementLabels';
import type { DestinationType, LineInput, LineRow } from '../../types/Procurement';

const DESTINATIONS: Array<{ value: DestinationType; label: string }> = [
  { value: 'favour', label: 'Favour' },
  { value: 'asoebi', label: 'Asoebi' },
  { value: 'decor', label: 'Decor' },
  { value: 'internal', label: 'Internal use' },
  { value: 'prize', label: 'Prize' },
];

interface Props {
  lines: LineRow[];
  currency: string | null;
  canEdit: boolean;
  canViewFinancials: boolean;
  onAdd: (input: LineInput) => Promise<void>;
  onUpdate: (id: string, input: LineInput) => Promise<void>;
  onRemove: (line: LineRow) => Promise<void>;
}

interface Draft {
  id: string | null;
  product_name: string;
  quantity: string;
  unit_price: string;
  pack_size: string;
  pack_unit: string;
  destination_type: string;
  dimensions_cm: string;
  notes: string;
}

const blank: Draft = { id: null, product_name: '', quantity: '1', unit_price: '', pack_size: '', pack_unit: '', destination_type: '', dimensions_cm: '', notes: '' };

const fromLine = (l: LineRow): Draft => ({
  id: l.id,
  product_name: l.product_name,
  quantity: String(l.quantity ?? ''),
  unit_price: l.unit_price ?? '',
  pack_size: l.pack_size == null ? '' : String(l.pack_size),
  pack_unit: l.pack_unit ?? '',
  destination_type: l.destination_type ?? '',
  dimensions_cm: l.dimensions_cm ?? '',
  notes: l.notes ?? '',
});

/** A line as the API wants it. A person who may not see money never sends a price (it would overwrite the real one with nothing). */
export function lineInputFrom(d: Draft, canViewFinancials: boolean): LineInput {
  const input: LineInput = {
    product_name: d.product_name.trim(),
    quantity: d.quantity.trim(),
    pack_size: d.pack_size.trim() || null,
    pack_unit: d.pack_unit.trim() || null,
    destination_type: (d.destination_type || null) as DestinationType | null,
    dimensions_cm: d.dimensions_cm.trim() || null,
    notes: d.notes.trim() || null,
  };
  if (canViewFinancials) input.unit_price = d.unit_price.trim() || null;
  return input;
}

const OrderLines: React.FC<Props> = ({ lines, currency, canEdit, canViewFinancials, onAdd, onUpdate, onRemove }) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!draft) return;
    if (!draft.product_name.trim()) { setError('Give the product a name.'); return; }
    if (!(Number(draft.quantity) > 0)) { setError('Quantity must be more than zero.'); return; }
    setSaving(true);
    setError(null);
    try {
      const input = lineInputFrom(draft, canViewFinancials);
      if (draft.id) await onUpdate(draft.id, input); else await onAdd(input);
      setDraft(null);
    } catch {
      setError('The line could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProcSection
      title="Lines"
      ariaLabel="Order lines"
      action={canEdit ? <Button variant="secondary" size="sm" icon={<Icon name="plus" size={14} />} onClick={() => { setError(null); setDraft(blank); }}>Add line</Button> : undefined}
    >
      {lines.length === 0 ? (
        <p className="text-subtle">No lines yet.</p>
      ) : (
        <div className="proc-scroll">
          <table className="proc-table">
            <thead>
              <tr>
                <th>Product</th><th className="proc-num">Qty</th><th className="proc-num">Received</th><th>Pack</th><th>For</th>
                {canViewFinancials && <><th className="proc-num">Unit price</th><th className="proc-num">Line total</th></>}
                {canEdit && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {lines.map(l => (
                <tr key={l.id}>
                  <td>{l.product_name}</td>
                  <td className="proc-num">{fmtQty(l.quantity)}</td>
                  <td className="proc-num">{fmtQty(l.quantity_received)}</td>
                  <td>{l.pack_size ? `${fmtQty(l.pack_size)} ${l.pack_unit ?? ''}`.trim() : '—'}</td>
                  <td>{DESTINATIONS.find(d => d.value === l.destination_type)?.label ?? '—'}</td>
                  {canViewFinancials && (
                    <>
                      <td className="proc-num">{formatDecimal(l.unit_price, l.currency ?? currency)}</td>
                      <td className="proc-num">{formatDecimal(l.line_total, l.currency ?? currency)}</td>
                    </>
                  )}
                  {canEdit && (
                    <td className="proc-row-actions">
                      <button type="button" className="btn-icon" title="Edit line" aria-label={`Edit ${l.product_name}`} onClick={() => { setError(null); setDraft(fromLine(l)); }}><Icon name="edit" size={14} /></button>
                      <button type="button" className="btn-icon dt-row-action--danger" title="Remove line" aria-label={`Remove ${l.product_name}`} onClick={() => void onRemove(l)}><Icon name="trash" size={14} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit line' : 'Add line'}
        size="small"
        footer={(
          <div className="modal-actions">
            <Button variant="primary" loading={saving} onClick={() => void save()}>Save line</Button>
            <Button variant="secondary" onClick={() => setDraft(null)}>Cancel</Button>
          </div>
        )}
      >
        {draft && (
          <>
            <div className="form-row">
              <FormInput label="Product" required value={draft.product_name} onChange={e => setDraft({ ...draft, product_name: e.target.value })} />
              <FormInput label="Quantity" required inputMode="decimal" value={draft.quantity} onChange={e => setDraft({ ...draft, quantity: e.target.value })} />
            </div>
            <div className="form-row">
              {canViewFinancials && (
                <FormInput label={`Unit price${currency ? ` (${currency})` : ''}`} inputMode="decimal" value={draft.unit_price} onChange={e => setDraft({ ...draft, unit_price: e.target.value })} />
              )}
              <FormSelect label="Destination" value={draft.destination_type} onChange={e => setDraft({ ...draft, destination_type: e.target.value })} options={[{ value: '', label: 'Not set' }, ...DESTINATIONS]} />
            </div>
            <div className="form-row">
              <FormInput label="Pack size" inputMode="decimal" value={draft.pack_size} onChange={e => setDraft({ ...draft, pack_size: e.target.value })} />
              <FormInput label="Pack unit" value={draft.pack_unit} onChange={e => setDraft({ ...draft, pack_unit: e.target.value })} />
              <FormInput label="Dimensions (cm)" value={draft.dimensions_cm} onChange={e => setDraft({ ...draft, dimensions_cm: e.target.value })} />
            </div>
            <FormTextarea label="Notes" rows={2} value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} />
            {error && <p role="alert" className="form-error-text">{error}</p>}
          </>
        )}
      </Modal>
    </ProcSection>
  );
};

export default OrderLines;
